import { useMediaQuery } from '@mui/material';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage, useOnClickOutside } from 'usehooks-ts';

import { useOutsideClick } from '../../../common/OutsideClick/OutsideClick';
import { setLocalStorage } from '../../../common/util/localStorage';
import { CustomButton, Icon, Input } from '../../../components';
import { useActions } from '../../../hooks/useActions';
import { useLayout } from '../../../hooks/useLayout';
import { useSearch } from '../../../hooks/useSearch';

import { LiveSearch } from './LiveSearch/LiveSearch';
import './Search.sass';
import { CheckBox } from '@mui/icons-material';

//Поиск input с кнопкой Найти
export const Search = () => {
  const navigate = useNavigate();
  const {
    setNavigationPath,
    setHandleEnter,
    setHandleSearch,
    setSearchValue,
    setShowMenu,
    setVisibleHistory,
    setMenuVisible,
  } = useActions();
  const { value, showMenu, handleSearch, visibleHistory, handleEnter } = useSearch();
  const { page } = useLayout();

  const mobileScreen = useMediaQuery('(max-width:900px)');

  const [searchHistory, setSearchHistory] = useLocalStorage<string[]>(
    'SEARCH_ADD_HISTORY',
    []
  );

  //url параметры
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  
  //Поиск input
  const [searchValueInput, setSearchValueInput] = useState('');
  
  //Поиск change
  const onChangeValueInput = (e: any) => {
    const changeValue = e.target.value;
    setSearchValueInput(changeValue);

    //onChangeEvent(changeValue);
    if (changeValue) {
      setVisibleHistory(false);
    } else {
      setVisibleHistory(true);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSearchValue(changeValue); //Добавляем в Redux
    if (mobileScreen) document.body.style.overflow = 'hidden'; //Закрываем скролл. Так как на моб. версии скролл убираем
    if (changeValue?.length > 0) setShowMenu(true); //Открываем окно поиска
    else setShowMenu(false); //Закрываем окно поиска

  };

  //История
  const addHistoryValue = (val: string) => {
    searchHistory.push('');
    let history = [...searchHistory];
    history.unshift(val);
    let filterHistory = history.filter((history) => history.trim() !== '');
    filterHistory = Array.from(new Set(filterHistory));
    if (filterHistory.length >= 12)
      filterHistory = filterHistory.slice(-12, -1);
    setSearchHistory(filterHistory);
  };
  //Срабатывает при нажатии на кнопку "найти" на пк версии
  const onAddSearchValue = () => {
    if (value !== '') {
      addHistoryValue(value);
      setHandleSearch(!handleSearch);
      setShowMenu(false);
      setSearchValue(value);
      navigate(`/search?q=${value}`);
    } else {
      return;
    }
  };

  //Key enter
  const onKeyDownHandle=(e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter"){
      let v = (e.target as any).value;
      if(v != ""){
        addHistoryValue(v);
        setHandleSearch(!handleSearch);
        setShowMenu(false);
        setSearchValue(v);
        navigate(`/search?q=${v}`);
      }

      //Отмена submit Form
      e.preventDefault();
    }
  }

  const onBlurHistorySearch = (value: string) => {
    if (mobileScreen) document.body.style.overflow = 'visible';
    navigate(`/search?q=${value}`);
    setShowMenu(false);
    setVisibleHistory(false);
    setSearchValue('');
    addHistoryValue(value);
  };
  //Клик вне или закрыть
  const onCloseClick = () => {
    if (showMenu || visibleHistory) {
      setShowMenu(false);
      setVisibleHistory(false);
      if (mobileScreen) document.body.style.overflow = 'visible';
    }
  };

  //Очистить при клике
  const onCloseOrClearSearchValue = () => {
    setSearchValueInput(''); //Очищаем инпут
    setSearchValue(''); //Очищаем инпут
    if (mobileScreen) {
      setShowMenu(false); //Закрываем окно поиска
      setVisibleHistory(false);
      document.body.style.overflow = 'visible';
      setLocalStorage.saveToLocalStorage('searchValue', '');
    } //Показываем скролл. Так как на моб. версии скролл убираем
  };

  const onFocusForm = () => {
    if (mobileScreen) {
      setNavigationPath('search');
      setMenuVisible(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setVisibleHistory(true);
      document.body.style.overflow = 'hidden';
    }
    if (value !== '') setShowMenu(true); //Открываем окно поиска
  };
  const onBlurForm = () => {
    if (value === '' && mobileScreen) {
      document.body.style.overflow = 'visible';
    }
  };
  useEffect(() => {
    if (page === 'searchPage' || page === 'cardPage') {
      setSearchValue(value);
      setSearchValueInput(searchQuery);
    } else {
      //Очищаем инпут
      setSearchValue('');
      setShowMenu(false);
      setSearchValueInput('');
    }
  }, [page]);

  const ref = useRef(null);

  useOutsideClick(ref, onCloseClick, mobileScreen ? visibleHistory : showMenu);
  return (
    <div className="search__wrapper" ref={ref}>
      <div className="search__wrapper-top">
        <form className="search">
          <input
            value={searchValueInput}
            className={classNames('input', { search_mobile: mobileScreen })}
            placeholder={
              visibleHistory && mobileScreen
                ? 'Я ищу...'
                : 'Поиск среди 900 000 товаров'
            }
            onChange={onChangeValueInput}
            onFocus={onFocusForm}
            onBlur={onBlurForm}
            onKeyDown={onKeyDownHandle}
          />
          {searchValueInput ? (
            <Icon
              onClick={onCloseOrClearSearchValue}
              variant="shape"
              className="desktop search__icon"
            />
          ) : null}
          <CustomButton
            variant="orange"
            className="search__button desktop"
            onClick={onAddSearchValue}
          >
            Найти
          </CustomButton>
          {searchValueInput.length === 0 && !visibleHistory ? (
            <Icon
              variant="searchGray"
              className="mobile search__icon"
              onClick={onFocusForm}
            />
          ) : (
            <Icon
              variant="closeGray"
              className="mobile search__icon close"
              onClick={onCloseOrClearSearchValue}
            />
          )}
        </form>
        {mobileScreen && value.length !== 0 && visibleHistory && (
          <p onClick={onCloseOrClearSearchValue}>Отмена</p>
        )}
      </div>
      {/* Поиск. Выпадающий список */}
      {showMenu && <LiveSearch inputValue={searchValueInput} />}
      {mobileScreen && visibleHistory && searchHistory.length !== 0 && (
        <div className="search_history">
          <ul className="search_history__lists">
            {searchHistory.map((item: any) => {
              return (
                /*<li onClick={() => goToSearchPage(item)} className="search_history__list">
                  {item}
                </li>*/
                <li
                  onClick={() => onBlurHistorySearch(item)}
                  className="search_history__list"
                >
                  {item}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
